import {makeRestMock} from '@translated/msw-utils'
import {HttpResponse} from 'msw'
import {z} from 'zod'

import {envVars} from '@/envVars'
import {F, langsPool, nullable} from '@/faker'
import {
  type CurrenciesResponseSchema,
  type CustomerResponseSchema,
  JOB_TYPE_VALUES,
  JobTypesOptionsResponseSchema,
  type OfferResponseSchema,
  RequestSchema,
  RequestUnitType,
} from '@/features/offer/services/schema'
import {raise} from '@/utils/raise'

const makeOfferMock = ({
  offerStatId,
  isAccepted,
}: {
  isAccepted?: boolean
  offerStatId: number
}): OfferResponseSchema => {
  const isOfferAccepted = isAccepted ?? F.datatype.boolean({probability: 0.25})

  return {
    customer: {
      id: F.string.uuid(),
      name: F.word.sample(),
      addressLine1: nullable(() => F.location.streetAddress()),
      addressLine2: nullable(() => F.location.streetAddress()),
      city: nullable(() => F.location.city()),
      country: nullable(() => F.location.country()),
      idReferrer: nullable(() => F.number.int()),
      currency: F.finance.currencyCode(),
      contactTel: nullable(() => F.phone.number()),
    },
    project: {
      id: F.number.int(),
      name: F.word.sample(),
      quoteCurrency: nullable(() => F.finance.currencyCode()),
      customerTimezone: F.number.int({min: -12, max: 12}),
    },
    offer: {
      id: F.number.int(),
      offerStatId,
      createDate: nullable(() => F.date.recent().toISOString()),
      pass: F.string.uuid(),
      accepted: isOfferAccepted,
      deliveryDays: F.number.float({multipleOf: 0.1, min: 0, max: 10}),
      expireAt: nullable(() => F.date.future().toISOString()),
      acceptingIp: nullable(() => F.internet.ip(), isOfferAccepted ? 1 : 0),
      acceptingDateTime: nullable(
        () => F.date.recent().toISOString(),
        isOfferAccepted ? 1 : 0,
      ),
      fileList: F.helpers.arrayElements(
        F.word.words({count: F.number.int({min: 0, max: 10})}).split(' '),
      ),
      lastVisitDateTime: nullable(() => F.date.recent().toISOString()),
      customTerms: nullable(() => F.lorem.sentence()),
      notes: nullable(() => F.lorem.sentence()),
      viewedAfterAccepted: nullable(() => F.datatype.boolean()),
      inLavorazioneOnAccepted: nullable(() => F.datatype.boolean()),
    },
    offerStat: {
      id: F.number.int(),
      currentOffer: nullable(() => F.number.int()),
      lang: F.helpers.arrayElement(langsPool.map((lang) => lang.code)),
      notarisation: F.datatype.boolean(),
      requesterEmails: F.helpers.arrayElements(
        Array.from({length: F.number.int({min: 1, max: 5})}).map(() =>
          F.internet.email(),
        ),
      ),
      requesterName: nullable(() => F.person.fullName()),
      requesterTel: nullable(() => F.phone.number()),
    },
    offerTrack: {
      hash: F.string.uuid(),
    },
    am: {
      id: F.string.uuid(),
      fullName: F.person.fullName(),
      title: nullable(() => F.lorem.words({min: 1, max: 4})),
      website: nullable(() => F.internet.url()),
      mainPhone: nullable(() => F.phone.number()),
      directPhone: nullable(() => F.phone.number()),
      fax: nullable(() => F.phone.number()),
      email: nullable(() => F.internet.email()),
    },
    jobs: Array.from({length: F.number.int({min: 1, max: 20})}).map(
      (_, index) => ({
        id: index + 1,
        projectId: 1,
        jobType: F.helpers.arrayElement(JOB_TYPE_VALUES),
        subType: nullable(() => F.word.sample()),
        source:
          langsPool[F.number.int({min: 0, max: langsPool.length - 1})]?.label ??
          raise('lang label not found'),
        target:
          langsPool[F.number.int({min: 0, max: langsPool.length - 1})]?.label ??
          raise('lang label not found'),
        customerWords: F.number.int({max: 999_999_999}),
        translatorWords: F.number.int({max: 999_999_999}),
        orderDate: nullable(() => F.date.recent().toISOString()),
        customerDeliveryDate: nullable(() => F.date.recent().toISOString()),
        customerTotal: F.number.float({
          multipleOf: 0.01,
          min: 0,
          max: 999_999,
        }),
        convertedCustomerTotal: F.number.float({
          multipleOf: 0.01,
          min: 0,
          max: 999_999,
        }),
        unitType: 'word',
      }),
    ),
    computedData: {
      ip: F.internet.ip(),
      projectServiceType: F.word.sample(),
      offerTotal: F.number.float({multipleOf: 0.01, min: 0, max: 999_999}),
      offerConvertedTotal: F.number.float({
        multipleOf: 0.01,
        min: 0,
        max: 999_999,
      }),
      hasOfferExpired: F.datatype.boolean(),
      showOfferConversion: F.datatype.boolean(),
      hasOfferFixedDate: F.helpers.maybe(() =>
        nullable(() => F.datatype.boolean()),
      ),
      offerDeliveryDate: nullable(() => F.date.future().toISOString()),
      offerExpireAt: nullable(() => F.date.future().toISOString()),
    },
    requests: Array.from({length: F.number.int({max: 20})}).map(() => ({
      id: F.number.int(),
      idContent: F.string.uuid(),
      serviceType: F.helpers.maybe(() => nullable(() => F.word.sample())),
      requestType: F.helpers.arrayElement(
        RequestSchema.shape.requestType.options,
      ),
      customerTotal: F.number.float({multipleOf: 0.01, min: 0, max: 999_999}),
      convertedCustomerTotal: F.number.float({
        multipleOf: 0.01,
        min: 0,
        max: 999_999,
      }),
      contentFilename: F.helpers.maybe(
        () => nullable(() => F.system.fileName(), 0.8),
        {probability: 0.8},
      ),
      source: F.helpers.maybe(() =>
        nullable(
          () =>
            langsPool[F.number.int({min: 0, max: langsPool.length - 1})]
              ?.label ?? raise('lang label not found'),
        ),
      ),
      target: F.helpers.maybe(() =>
        nullable(
          () =>
            langsPool[F.number.int({min: 0, max: langsPool.length - 1})]
              ?.label ?? raise('lang label not found'),
        ),
      ),
      jobType: F.helpers.maybe(() =>
        nullable(() => F.helpers.arrayElement(JOB_TYPE_VALUES)),
      ),
      quantity: F.helpers.maybe(
        () => nullable(() => F.number.int({max: 999_999}), 0.8),
        {probability: 0.8},
      ),
      fileWcRaw: F.helpers.maybe(
        () => nullable(() => F.number.int({max: 999_999}), 0.8),
        {probability: 0.8},
      ),
      unitType: F.helpers.maybe(
        () =>
          nullable(() => F.helpers.arrayElement(RequestUnitType.options), 0.8),
        {probability: 0.8},
      ),
      unitPrice: F.helpers.maybe(() =>
        nullable(() => F.number.float({multipleOf: 0.01})),
      ),
      convertedUnitPrice: F.helpers.maybe(() =>
        nullable(() => F.number.float({multipleOf: 0.01})),
      ),
    })),
  }
}

export const makeFetchOfferMock = makeRestMock<OfferResponseSchema>({
  method: 'get',
  path: `${envVars.VITE_API_URL}/v1/offers/:id`,
})

export const makeFetchOfferMockInternalMock = makeRestMock<OfferResponseSchema>(
  {
    method: 'get',
    path: `${envVars.VITE_INTERNAL_API_URL}/v1/offers/:id`,
  },
)

export const makePatchOfferMock = makeRestMock<OfferResponseSchema>({
  method: 'patch',
  path: `${envVars.VITE_API_URL}/v1/offers/:id`,
})

export const makePostOfferMock = makeRestMock<OfferResponseSchema>({
  method: 'post',
  path: `${envVars.VITE_API_URL}/v1/offers/:id/accept`,
})

export const makeDownloadPdfMock = makeRestMock({
  method: 'get',
  path: `${envVars.VITE_API_URL}/v1/offers/:id/pdf`,
})

export const makeTrackPurchaseMock = makeRestMock({
  method: 'get',
  path: `${envVars.VITE_HB_PROXY}`,
})

export const makeGetCustomerTypeMock = makeRestMock<CustomerResponseSchema>({
  method: 'get',
  path: `${envVars.VITE_API_URL}/v1/customersTypes`,
})

export const makeFetchCurrenciesMock = makeRestMock<CurrenciesResponseSchema>({
  method: 'get',
  path: `${envVars.VITE_API_URL}/v1/offers/:id/currencies`,
})

export const makeFetchCurrenciesInternalMock =
  makeRestMock<CurrenciesResponseSchema>({
    method: 'get',
    path: `${envVars.VITE_INTERNAL_API_URL}/v1/offers/:id/currencies`,
  })

export const makeFetchJobTypesMock = makeRestMock({
  method: 'get',
  path: `${envVars.VITE_JSON_BASEPATH}/:language/job-types.json`,
})

export const makeFetchJobTypesOptionsMock = makeRestMock({
  method: 'get',
  path: `${envVars.VITE_JSON_BASEPATH}/job-types-options.json`,
})

export const handlers = [
  makeFetchOfferMock((info) => {
    const params = z.object({id: z.coerce.number().int()}).parse(info.params)
    return HttpResponse.json(makeOfferMock({offerStatId: params.id}))
  }),
  makeFetchOfferMockInternalMock((info) => {
    const params = z.object({id: z.coerce.number().int()}).parse(info.params)
    return HttpResponse.json(makeOfferMock({offerStatId: params.id}))
  }),
  makePatchOfferMock((info) => {
    const params = z.object({id: z.coerce.number().int()}).parse(info.params)
    return HttpResponse.json(makeOfferMock({offerStatId: params.id}))
  }),
  makePostOfferMock((info) => {
    const params = z.object({id: z.coerce.number().int()}).parse(info.params)
    return HttpResponse.json(
      makeOfferMock({isAccepted: true, offerStatId: params.id}),
    )
  }),
  makeDownloadPdfMock(() => HttpResponse.json()),
  makeTrackPurchaseMock(() => HttpResponse.json()),
  makeGetCustomerTypeMock(() =>
    HttpResponse.json({customerType: F.word.sample()}),
  ),
  makeFetchCurrenciesMock(() => {
    return HttpResponse.json(
      Array.from({length: F.number.int({min: 1, max: 20})}).map(() => ({
        currency: F.finance.currencyCode(),
        rate: F.number.float({min: 0, max: 200}),
      })),
    )
  }),
  makeFetchCurrenciesInternalMock(() => {
    return HttpResponse.json(
      Array.from({length: F.number.int({min: 1, max: 20})}).map(() => ({
        currency: F.finance.currencyCode(),
        rate: F.number.float({min: 0, max: 200}),
      })),
    )
  }),
  makeFetchJobTypesMock(() => {
    return HttpResponse.json({
      ASSEVERAZIONE: 'Sworn translation service',
      BOLLI: 'Fiscal stamps',
      CERTIFICATION: 'Certification',
      COPYREVIEW: 'Copy review',
      CREDITNOTE: 'Credit note',
      DISCOUNT: 'Discount',
      DTP: 'DTP',
      EMBEDDING: 'Embedding',
      POSTEDITING: 'Postediting',
      REVISIONE: 'Revision',
      'SOUND-EDITING': 'Sound Editing',
      SPEDIZIONE: 'Shipping',
      SUBTITLING: 'Subtitling',
      TRADUZIONE: 'Translation',
      TRANSCR: 'Transcription',
      TRANSCREATION: 'Transcreation',
      'video-bundle': 'Video Bundle',
      'VIDEO-EDITING': 'Video Editing',
      VOICEOVER: 'Voice Over',
      SUBTITLING_REVISION: 'Subtitling revision',
      VOICEOVER_REVISION: 'Voiceover revision',
      TRANSCREATION_REVISION: 'Transcreation revision',
      FINANZIARIO: '--',
      FORNITORE: '--',
      mymemorypluginpro: '--',
      PROMOCODE: '--',
      'SPESEBANC.': '--',
      'STAMP DUTY': 'Bolli',
      UNKNOWN: '--',
      VALUTAZIONE: '--',
      'AI-LABELLING': '--',
      IMPAGINAZIONE: '--',
      QA: '--',
      'SPECIAL DISCOUNT': 'Discount',
      'QUALITY DISCOUNT': 'Discount',
      'DELAY DISCOUNT': 'Discount',
      'GOODWILL DISCOUNT': 'Discount',
      DUBBING: 'Dubbing',
      LQA: 'LQA',
      SWORN_TRANSLATION: 'Translation',
      AUDIO_TRANSCRPT: 'Transcription',
      'REVISIONE~second_pass': 'Second pass revision',
      'TRADUZIONE~adaptation': 'Adaptation',
      MMT_MACHINE_TRANSLATION: 'Machine translation',
      RLHF_ANNOTATION: 'RLHF Annotation',
      RLHF_ANNOTATION_REVISION: 'RLHF Annotation Revision',
      SFT_ANNOTATION: 'SFT Annotation',
      SFT_ANNOTATION_REVISION: 'SFT Annotation Revision',
    })
  }),
  makeFetchJobTypesOptionsMock(() => {
    return HttpResponse.json(
      Object.fromEntries(
        JobTypesOptionsResponseSchema.keyof().options.map((job) => [
          job,
          {
            showSourceLanguage: F.datatype.boolean(),
            showTargetLanguage: F.datatype.boolean(),
          },
        ]),
      ),
    )
  }),
]
